// Lib
import { get, map, uniq, isEmpty, reduce as reduceFp, forEach as forEachFp } from 'lodash/fp';

// Utils
import { chooseSingular, listSeparator, pluralise } from '../utils/stringUtil';
import { getElementType } from '../elements/utils/elementPropertyUtils';
import { getElementDisplayName } from '../elements/elementRegistry';
import { getElementTypeTranslationKey } from '../utils/i18n/coreTranslationHelpers';

// Constants
import { BOARD_DEFAULT_TITLE } from '../boards/boardConstants';
import { isBoard, isSketch, isTask } from '../elements/utils/elementTypeUtils';
import { ElementChangeType } from './notificationConstants';
import { ElementType } from '../elements/elementTypes';

const CHANGE_TYPE_SORT_ORDER = ['Added', 'Updated', 'Completed'];
const ELEMENT_TYPE_SORT_ORDER = [
    ElementType.BOARD_TYPE,
    ElementType.CARD_TYPE,
    ElementType.IMAGE_TYPE,
    ElementType.DOCUMENT_TYPE,
    ElementType.FILE_TYPE,
    ElementType.LINK_TYPE,
    ElementType.TASK_LIST_TYPE,
    ElementType.TASK_TYPE,
    ElementType.LINE_TYPE,
    ElementType.COLUMN_TYPE,
    ElementType.COMMENT_THREAD_TYPE,
];

const reduce = reduceFp.convert({ cap: false });
const forEach = forEachFp.convert({ cap: false });

const updateSummarySortFn = (summaryA, summaryB) => {
    const changeTypeIndexA = CHANGE_TYPE_SORT_ORDER.indexOf(summaryA.changeType);
    const changeTypeIndexB = CHANGE_TYPE_SORT_ORDER.indexOf(summaryB.changeType);

    if (changeTypeIndexA !== changeTypeIndexB) return changeTypeIndexA - changeTypeIndexB;

    const elementTypeIndexA = ELEMENT_TYPE_SORT_ORDER.indexOf(summaryA.elementType);
    const elementTypeIndexB = ELEMENT_TYPE_SORT_ORDER.indexOf(summaryB.elementType);

    return elementTypeIndexA - elementTypeIndexB;
};

const changesValid = (changes) => {
    if (!changes) {
        console.warn('No changes to check');
        return false;
    }

    return true;
};

const isTaskCompletionChange = ({ elementType, changes }) => {
    if (!changesValid(changes)) return false;
    return isTask(elementType) && changes.some((change) => change.changeType === ElementChangeType.COMPLETE);
};

export const getChangeType = ({ elementType, changes }) => {
    if (!changesValid(changes)) return null;

    if (isTask(elementType) && changes.every((change) => change.changeType === ElementChangeType.COMPLETE)) {
        return null;
    }

    return changes.some(
        (change) => change.changeType === ElementChangeType.CREATE || change.changeType === ElementChangeType.MOVE_IN,
    )
        ? 'Added'
        : 'Updated';
};

const isNewBoardChangeWithoutCustomTitle = (changeEntry) =>
    changeEntry &&
    changeEntry.isNewBoard &&
    (isEmpty(changeEntry.boardTitle) || changeEntry.boardTitle.toLowerCase() === BOARD_DEFAULT_TITLE.toLowerCase());

export const getUpdateSummaryList = (elementChangesMap) => {
    const changeCountMap = reduce(
        (acc, changeEntry) => {
            const { elementType, changes } = changeEntry;

            // Don't include new board changes that don't have custom titles
            if (isNewBoardChangeWithoutCustomTitle(changeEntry)) return acc;

            acc[elementType] = acc[elementType] || {};

            const changeType = getChangeType({ elementType, changes });

            if (changeType) {
                acc[elementType][changeType] = (acc[elementType][changeType] || 0) + 1;
            }

            if (isTaskCompletionChange({ elementType, changes })) {
                acc[elementType].Completed = (acc[elementType].Completed || 0) + 1;
            }

            return acc;
        },
        {},
        elementChangesMap,
    );

    return reduce(
        (descriptionList, elementTypeChangeCounts, elementType) => {
            forEach((count, changeType) => {
                if (!count) return;

                descriptionList.push({
                    elementType,
                    groupName: elementType.toLowerCase(),
                    changeType,
                    count,
                });
            }, elementTypeChangeCounts);

            return descriptionList;
        },
        [],
        changeCountMap,
    ).sort(updateSummarySortFn);
};

export const getNewBoardTitlesWithBoardIds = (elementChanges) =>
    reduce(
        (newBoardTitles, changeEntry) => {
            const { _id, elementType, isNewBoard, boardTitle } = changeEntry;

            if (isBoard(elementType) && isNewBoard && !isNewBoardChangeWithoutCustomTitle(changeEntry)) {
                newBoardTitles.push({ boardId: _id, title: boardTitle });
            }

            return newBoardTitles;
        },
        [],
        elementChanges,
    );

export const getNewBoardTitles = (elementChanges) => getNewBoardTitlesWithBoardIds(elementChanges).map(get('title'));

export const getElementChangeElementTypes = (elementChanges) => {
    const elementTypes = map(getElementType, elementChanges);
    return uniq(elementTypes);
};

export const getElementChangeElementTypeDisplayName = ({ elementType, count }, notificationsTranslate) => {
    if (isSketch(elementType)) {
        // For sketches, use specific translation key from elements namespace
        const sketchKey =
            count === 1
                ? 'elements:elementTypes.sketch.sketch_one'
                : 'elements:elementTypes.sketch.sketch_other';
        const fallback = chooseSingular(count, 'sketch', 'sketches');
        const translated = notificationsTranslate ? notificationsTranslate(sketchKey) : undefined;
        return translated || fallback;
    }

    // Get the translation key using the centralized mapping
    const translationKey = getElementTypeTranslationKey(elementType);
    // Build the proper translation key with pluralization using elements namespace
    const pluralSuffix = count === 1 ? '_one' : '_other';
    const fullKey = `elements:elementTypes.${translationKey}.${translationKey}${pluralSuffix}`;

    // Get fallback text
    let fallbackText = getElementDisplayName(elementType).toLowerCase();

    if (isTask(elementType)) {
        // For tasks, use taskItem translation key
        const taskKey =
            count === 1
                ? 'elements:elementTypes.taskItem.taskItem_one'
                : 'elements:elementTypes.taskItem.taskItem_other';
        const taskFallback = `${fallbackText} item`;
        const translated = notificationsTranslate ? notificationsTranslate(taskKey) : undefined;
        return translated || taskFallback;
    }

    fallbackText = pluralise(fallbackText, count);

    // Use translation if available
    const translated = notificationsTranslate ? notificationsTranslate(fullKey) : undefined;
    return translated || fallbackText;
};

const defaultNotificationsTranslate = () => undefined;

export const describeChanges = (elementChanges, notificationsTranslate = defaultNotificationsTranslate) => {
    if (isEmpty(elementChanges)) {
        const conjunction = notificationsTranslate('conjunctions.in') || 'in';
        return { conjunction };
    }

    const updateSummaryList = getUpdateSummaryList(elementChanges);

    const isSingleAddedChange = updateSummaryList.length === 1 && updateSummaryList[0].changeType === 'Added';
    const conjunctionKey = isSingleAddedChange ? 'conjunctions.to' : 'conjunctions.in';
    const conjunctionDefault = isSingleAddedChange ? 'to' : 'in';
    const conjunction = notificationsTranslate(conjunctionKey) || conjunctionDefault;

    const finalConjunction = notificationsTranslate('conjunctions.and') || 'and';

    let sentence = '';

    updateSummaryList.forEach((updateSummary, index) => {
        const { count, changeType, elementType } = updateSummary;

        const separator = listSeparator(index, updateSummaryList.length, finalConjunction);

        const isFirstEntry = index === 0;

        const elementTypeDisplayName = isFirstEntry
            ? getElementChangeElementTypeDisplayName({ elementType, count }, notificationsTranslate)
            : '';
        const elementTypeDescriptor = isFirstEntry ? ` ${elementTypeDisplayName}` : '';

        const changeTypeKey = `changeTypes.${changeType.toLowerCase()}`;
        const changeTypeText = notificationsTranslate(changeTypeKey) || changeType.toLowerCase();

        sentence += `${changeTypeText} ${count}${elementTypeDescriptor}${separator}`;
    });

    return {
        sentence,
        conjunction,
    };
};
